import {
  Component,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable, Observer, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';
import { CommonArrayService } from 'src/app/core/services/common-array.service';
import { environment } from 'src/environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Test } from 'src/app/core/interfaces/test';
import { ExamCategory } from 'src/app/core/interfaces/exam-category';
import { LocalArray } from 'src/app/core/interfaces/local-array';
import { Exam } from 'src/app/core/interfaces/exam';
import { TestSeries } from 'src/app/core/interfaces/test-series';
import { CkEditorConfigService } from 'src/app/core/services/ck-editor-config.service';
import { NgxSuneditorComponent } from 'ngx-suneditor';

@Component({
  selector: 'app-add-update-test-series',
  templateUrl: './add-update-test-series.component.html',
  styleUrls: ['./add-update-test-series.component.scss'],
})
export class AddUpdateTestSeriesComponent implements OnInit {
  @ViewChildren(NgxSuneditorComponent)
  editors!: QueryList<NgxSuneditorComponent>;
  addUpdateTestSeriesForm: FormGroup;
  idForUpdate: string;
  buttonLoading = false;
  imageLoading = false;
  allExamCategories: ExamCategory[] = [];
  checkAddPermission = false;
  checkUpdatePermission = false;
  search = '';
  searchExam: Subject<string> = new Subject<string>();
  searchExamCategory: Subject<string> = new Subject<string>();
  allTestTypes: LocalArray[] = [];
  allLevels: LocalArray[] = [];
  time: Date | null = null;
  defaultOpenValue: any;
  selectedTests: Test[] = []; // Main source of truth - contains ALL selected tests
  mediaBasePath: string = '';
  loading: boolean = false;
  filterTests: Test[] = [];
  allTests: Test[] = [];
  totalDataCount: number = 0;
  allExams: Exam[] = [];
  switchValue: boolean = false;
  mediaBaseUrl = environment.mediaBaseUrl;
  mediaUploadUrl = environment.apiBaseUrl + '/api/media';
  examSearch: string = '';
  examCategorySearch: string = '';
  topicSearch: string = '';
  pageIndex: number = 1;
  pageSize: number = 20;
  limit: boolean = false;
  name = 'test';
  log: string = '';
  selectedExam: string = '';
  hasNextPage: boolean = true;
  public editorOptions: any;
  tabIndex: number = 0;
  newSelectedTests: any[] = []; // Display array - filtered view of selectedTests
  targetIndex: number | null = null;
  currentIndex: number | null = null;
  examFilterButtons: { examId: string; examName: string; count: number }[] = [];
  activeExamFilter: string | null = null;

  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private msg: NzMessageService,
    private commonArrayService: CommonArrayService,
    private ckEditorService: CkEditorConfigService
  ) {
    this.idForUpdate = this.activatedRoute.snapshot.params.id;
    this.tabIndex = this.activatedRoute.snapshot.queryParams.index;
    this.addUpdateTestSeriesForm = this.fb.group({
      name: [null, [Validators.required]],
      exam: ['', [Validators.required]],
      examCategory: ['', [Validators.required]],
      status: [true],
      difficultyLevel: [null],
      price: [0],
      description: [null],
      extraDescription: [null],
      isPaid: [false],
      durationTime: [null],
      tests: [[]],
      image: [null],
    });
  }

  ngOnInit() {
    this.editorOptions = this.ckEditorService.editorOptions;
    this.editorOptions.height = '100';
    this.allTestTypes = this.commonArrayService.testsTypes;
    this.allLevels = this.commonArrayService.difficultyLevel;

    if (this.idForUpdate) {
      this.getTestSeriesById();
    } else {
      this.getAllTests();
    }
    this.searchExam.pipe(debounceTime(1000)).subscribe((success: any) => {
      this.examSearch = success;
      this.getAllExams();
    });
    this.searchExamCategory
      .pipe(debounceTime(1000))
      .subscribe((success: any) => {
        this.examCategorySearch = success;
        this.getAllExamsCategory();
      });
    this.getAllExams();
    this.getAllExamsCategory();
  }

  onDescriptionChange(content: any) {
    this.addUpdateTestSeriesForm.patchValue(
      {
        description: content.content,
      },
      { emitEvent: false }
    );
  }

  onExtraDescriptionChange(content: any) {
    this.addUpdateTestSeriesForm.patchValue(
      {
        extraDescription: content.content,
      },
      { emitEvent: false }
    );
  }

  drop(event: CdkDragDrop<string[]>): void {
    // If filtering is active, we need to map back to original indices
    if (this.activeExamFilter) {
      const draggedItem = this.newSelectedTests[event.previousIndex];
      const targetItem = this.newSelectedTests[event.currentIndex];

      const draggedIndexInMain = this.selectedTests.findIndex(
        (t) => t._id === draggedItem._id
      );
      const targetIndexInMain = this.selectedTests.findIndex(
        (t) => t._id === targetItem._id
      );

      // Move in the main array
      moveItemInArray(
        this.selectedTests,
        draggedIndexInMain,
        targetIndexInMain
      );
    } else {
      // No filter active, work directly on selectedTests
      moveItemInArray(
        this.selectedTests,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Reindex the main array
    this.selectedTests.forEach((test, idx) => {
      test.index = idx + 1;
      test.isChangeToEditIndex = false;
    });

    // Apply current filter to update the view
    this.applyCurrentFilter();
  }

  changeIndex(item: any, oldIndex: number, newIndex: number) {
    const fromIndex = oldIndex - 1;
    const toIndex = newIndex - 1;

    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= this.selectedTests.length ||
      toIndex >= this.selectedTests.length
    ) {
      return;
    }

    // MOVE instead of SWAP
    moveItemInArray(this.selectedTests, fromIndex, toIndex);

    // Reindex after move
    this.selectedTests.forEach((x, idx) => {
      x.index = idx + 1;
      x.isChangeToEditIndex = false;
    });

    this.targetIndex = null;
    this.currentIndex = null;

    // Apply current filter if any
    this.applyCurrentFilter();
  }

  inputValueChanged(oldIndex: number, newIndex: number) {
    this.targetIndex = newIndex;
    this.currentIndex = oldIndex;
  }

  customRequestHeaders = () => {
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
  };

  clickChange(data: any) {
    this.switchValue = data;
  }

  getAllExams(): void {
    const params: any = {
      skip: 0,
      limit: 100,
      status: true,
      approveStatus: 'approved',
    };
    if (this.examSearch) {
      params.search = this.examSearch;
    } else {
      delete params.search;
    }
    this.httpRequestService.request('get', 'exams', params).subscribe(
      (result: any) => {
        this.allExams = result.data;
      },
      (err: any) => {}
    );
  }

  getAllExamsCategory(): void {
    const params: any = {
      skip: 0,
      limit: 100,
      status: true,
      approveStatus: 'approved',
    };
    if (this.examCategorySearch) {
      params.search = this.examCategorySearch;
    } else {
      delete params.search;
    }
    this.httpRequestService.request('get', 'exam-categories', params).subscribe(
      (result: any) => {
        this.allExamCategories = result.data;
      },
      (err: any) => {}
    );
  }

  onChangeDifficultyLevel(value: string, skip = 0): void {
    this.filterSectionValues();
  }

  getAllTests(skip = 0, sortBy?: any): void {
    let params: any;
    params = {
      skip,
      limit: this.pageSize,
      status: true,
      approveStatus: 'approved',
    };
    if (
      this.addUpdateTestSeriesForm &&
      this.addUpdateTestSeriesForm.value &&
      this.addUpdateTestSeriesForm.value.exam
    ) {
      params.exam = this.addUpdateTestSeriesForm.value.exam;
    } else {
      delete params.exam;
    }
    if (this.addUpdateTestSeriesForm.value.difficultyLevel) {
      params['difficultyLevel'] =
        this.addUpdateTestSeriesForm.value.difficultyLevel;
    } else {
      delete params.difficultyLevel;
    }

    if (this.search) {
      params.search = this.search;
    }
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }

    this.loading = true;
    this.httpRequestService
      .request('get', 'tests/get-tests-for-list', params)
      .subscribe(
        (result) => {
          this.hasNextPage = result.meta.hasNextPage;
          let data: Test[] = result.data;
          this.allTests.push(...data);
          this.totalDataCount = result.totalCount;
          this.filterSectionValues();
          this.loading = false;
        },
        (err) => {
          this.loading = false;
        }
      );
  }

  // FIXED: Remove tests - always work on selectedTests (main array)
  removeTests(): void {
    const removedItems = this.newSelectedTests.filter((x) => x.isChecked);
    const removedIds = removedItems.map((r: any) => r._id);

    // IMPORTANT: Remove from selectedTests (main array), not newSelectedTests
    this.selectedTests = this.selectedTests.filter(
      (item: any) => !removedIds.includes(item._id)
    );

    // Reindex the main array
    this.selectedTests.forEach((x, idx) => {
      x.index = idx + 1;
      x.isChangeToEditIndex = false;
    });

    // Add removed items back to allTests
    this.allTests = [...this.allTests, ...removedItems];
    this.allTests = this.allTests.map((item: any) =>
      removedIds.includes(item._id)
        ? { ...item, isChecked: false, isOpen: true }
        : item
    );

    this.filterSectionValues();
    this.updateExamFilterButtons();

    // Apply the current filter to newSelectedTests
    this.applyCurrentFilter();
  }

  // FIXED: Add tests - always work on selectedTests (main array)
  addTests(): void {
    const added = this.filterTests
      .filter((x: any) => x.isChecked)
      .map((x: any, index: number) => ({
        ...x,
        isChecked: false,
        questionType: 'NORMAL',
        key: index,
      }));

    // IMPORTANT: Add to selectedTests (main array), not newSelectedTests
    this.selectedTests = [...this.selectedTests, ...added];

    // Reindex the main array
    this.selectedTests.forEach((x, index) => {
      x.index = index + 1;
      x.isChangeToEditIndex = false;
    });

    this.updateExamFilterButtons();
    this.filterSectionValues();

    // Apply the current filter to newSelectedTests
    this.applyCurrentFilter();
  }

  filterSectionValues() {
    this.filterTests = this.allTests
      .filter((item: any) => {
        return !this.selectedTests.map((q: any) => q._id).includes(item._id);
      })
      .map((item: any) => ({ ...item, isChecked: false, isOpen: true }));
    const uniqueData: any = Array.from(
      new Set(this.filterTests.map((item) => item._id))
    ).map((id) => this.filterTests.find((obj) => obj._id === id));
    this.filterTests = [...uniqueData];
  }

  getTestSeriesById() {
    try {
      const data = this.httpRequestService
        .request('get', `test-series/list/${this.idForUpdate}`)
        .subscribe((data) => {
          const singleData: TestSeries = data.data;
          this.addUpdateTestSeriesForm.patchValue({
            name: singleData.name,
            exam: singleData.exam._id,
            price: singleData.price,
            durationTime: singleData.durationTime,
            isPaid: singleData.isPaid,
            image: singleData.image,
            examCategory: singleData.examCategory._id,
          });

          this.editors.forEach((editor: any) => {
            if (editor.editorID === 'description') {
              editor.setContents(singleData.description);
            } else if (editor.editorID === 'extraDescription') {
              editor.setContents(singleData.extraDescription);
            }
          });
          this.selectedTests = [
            ...singleData.tests.map((x: any, index: any) => ({
              ...x.test,
              isOpen: x.isOpen,
              index: index + 1,
              isChangeToEditIndex: false,
            })),
          ];
          this.newSelectedTests = [...this.selectedTests];
          this.updateExamFilterButtons();
          this.getAllTests();
        });
    } catch (error) {}
  }

  submit(): void {
    if (!this.addUpdateTestSeriesForm.valid) {
      this.markFormGroupTouched(this.addUpdateTestSeriesForm);
    } else {
      if (this.idForUpdate) {
        this.addOrUpdateTestSeries(
          'put',
          `test-series/${this.idForUpdate}`,
          'Test-Series Successfully Updated'
        );
      } else {
        this.addOrUpdateTestSeries(
          'post',
          'test-series',
          'Test-Series Added Successfully '
        );
      }
    }
  }

  addOrUpdateTestSeries(
    requestMethod: string,
    requestURL: string,
    successMessage: string
  ): void {
    let testData: any = [];
    this.selectedTests.forEach((data: any) => {
      testData.push({
        test: data._id,
        isOpen: data.isOpen,
      });
    });
    this.addUpdateTestSeriesForm.patchValue({
      tests: [...testData],
    });
    if (this.selectedTests && !this.selectedTests.length) {
      this.notificationService.error('', 'Add some tests');
      return;
    }
    this.buttonLoading = true;
    const finalData: TestSeries = this.addUpdateTestSeriesForm.value;
    if (finalData.isPaid === false) {
      finalData.price = 0;
      finalData.durationTime = 0;
    }
    this.httpRequestService
      .request(requestMethod, requestURL, finalData)
      .subscribe(
        (result: any) => {
          this.notificationService.success('', successMessage);
          this.router.navigate(['/main/test-series'], {
            queryParams: { index: this.tabIndex },
          });
          this.buttonLoading = false;
        },
        (error: any) => {
          if (error.error.errors) {
            const allErrors: string[] = Object.values(error.error.errors);
            for (const err of allErrors) {
              this.notificationService.error('', err);
            }
          } else {
            this.notificationService.error('', error.error.message);
          }
          this.buttonLoading = false;
        }
      );
  }

  UploadImageFile(
    info: { file: NzUploadFile },
    FormControl: string,
    index: number
  ) {
    switch (info.file.status) {
      case 'uploading':
        this.imageLoading = true;
        break;
      case 'done':
        this.imageLoading = false;
        this.addUpdateTestSeriesForm.patchValue({
          image: info.file.response.data.path,
        });
        break;
      case 'error':
        this.msg.error('Network error');
        this.imageLoading = false;
        break;
    }
  }

  beforeRelatedMediaUpload = (
    file: NzUploadFile,
    fileList: NzUploadFile[]
  ): Observable<any> => {
    return new Observable((observer: Observer<boolean>) => {
      const isImage =
        file.type === 'image/jpg' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/JPG' ||
        file.type === 'image/JPEG' ||
        file.type === 'image/PNG';
      if (!isImage) {
        this.msg.error('You can only upload image file!');
        observer.complete();
        return;
      }
      const isLt3M = file.size ? file.size / 1024 / 1024 < 3 : false;
      if (!isLt3M) {
        this.msg.error('Image must smaller than 3MB!');
        observer.complete();
        return;
      }
      observer.next(isImage && isLt3M);
      observer.complete();
    });
  };

  removeImage(path: string): void {
    let params: any;
    params = { path: path };
    this.addUpdateTestSeriesForm.patchValue({
      image: null,
    });
    this.httpRequestService
      .request('post', 'media/delete-media', params)
      .subscribe((result: any) => {
        if (result) {
          this.notificationService.success('', 'Image Deleted Successfully');
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const i in formGroup.controls) {
      if (formGroup.controls.hasOwnProperty(i)) {
        formGroup.controls[i].markAsDirty();
        formGroup.controls[i].updateValueAndValidity();
      }
    }
  }

  testPagination(): void {
    this.pageIndex = this.pageIndex + 1;
    this.getAllTests(this.pageSize * (this.pageIndex - 1));
  }

  searchExamForDropdown(event: any): void {
    this.searchExam.next(event);
  }

  searchExamCategoryForDropdown(event: any): void {
    this.searchExamCategory.next(event);
  }

  examChange(exam: any) {
    if (this.addUpdateTestSeriesForm?.value?.exam) {
      this.allTests = [];
      this.getAllTests();
    }
  }

  updateExamFilterButtons(): void {
    this.activeExamFilter = null;
    this.newSelectedTests = [...this.selectedTests];
    const examMap = new Map<string, { examName: string; count: number }>();

    for (const test of this.selectedTests) {
      let examId: string | undefined;
      let examName = 'Unknown Exam';

      if (test.exam && typeof test.exam === 'object' && '_id' in test.exam) {
        examId = (test.exam as any)._id;
        examName = (test.exam as any).examName || examName;
      } else if (typeof test.exam === 'string') {
        examId = test.exam;
      }

      if (!examId) continue;

      if (!examMap.has(examId)) {
        examMap.set(examId, { examName, count: 1 });
      } else {
        examMap.get(examId)!.count++;
      }
    }

    this.examFilterButtons = Array.from(examMap, ([examId, value]) => ({
      examId,
      examName: value.examName,
      count: value.count,
    }));
  }

  toggleExamFilter(examId: string): void {
    if (this.activeExamFilter === examId) {
      // Remove filter
      this.activeExamFilter = null;
    } else {
      // Apply filter
      this.activeExamFilter = examId;
    }

    // Use the helper method to apply filter consistently
    this.applyCurrentFilter();
  }

  // NEW HELPER METHOD: Consistently applies the current filter
  private applyCurrentFilter(): void {
    if (this.activeExamFilter) {
      this.newSelectedTests = this.selectedTests.filter((test: any) => {
        if (typeof test.exam === 'string') {
          return test.exam === this.activeExamFilter;
        } else if (
          test.exam &&
          typeof test.exam === 'object' &&
          '_id' in test.exam
        ) {
          return (test.exam as any)._id === this.activeExamFilter;
        }
        return false;
      });
    } else {
      this.newSelectedTests = [...this.selectedTests];
    }

    // Reindex the displayed array (for display purposes only)
    this.newSelectedTests.forEach((x, idx) => (x.index = idx + 1));
  }
}
